import { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { AccountBalanceOutlined } from "@mui/icons-material";
import PropTypes from "prop-types";
import DataTable from "components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { applySortingWithAccessorKeys } from "./utils/tableUtils.js";
import { TableEntityCell } from "../../theme/components/index.js";

export default function BankAccountTable({ pageTitle, pageSubtitle }) {
  const { enqueueSnackbar } = useSnackbar();

  // Tabellen-Konfiguration für useMultiTableData
  const tableKey = "bank_accounts";
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Bankkonten",
        route: "/data/bank-accounts",
        pageSize: 10,
        storageKey: "v2_table_bank_accounts",
        extraParams: {
          include: ["objekte", "kunde"],
        },
      },
    ],
    []
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );
  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];
  const totalRows = tableState.totalRows ?? rows.length;
  const pageIndex = tableState.pageIndex ?? 0;
  const pageSize = tableState.pageSize ?? 10;
  const isLoading = tableState.isLoading ?? false;
  const hasLoaded = tableState.hasLoaded ?? false;

  // fetchData kann hier bei Bedarf aufgerufen werden, z.B. in useEffect
  useEffect(() => {
    fetchData(tableKey);
  }, []);

  // Spalten-Definition
  const columns = useMemo(
    () => [
      {
        accessorKey: "iban",
        header: "IBAN",
        size: 240,
        Cell: ({ row }) => (
          <TableEntityCell
            title={row.original?.iban || "—"}
            subtitle={row.original?.bank_name || undefined}
            avatar={{ icon: <AccountBalanceOutlined /> }}
          />
        ),
      },
      { accessorKey: "bic", header: "BIC", size: 120 },
      { accessorKey: "account_holder", header: "Kontoinhaber", size: 180 },
      {
        accessorKey: "objekte_count",
        header: "Objekte",
        size: 100,
        Cell: ({ row }) =>
          Array.isArray(row.original.objekte) ? row.original.objekte.length : 0,
      },
      {
        accessorKey: "kunde.display_name",
        header: "Kunde",
        size: 180,
        Cell: ({ row }) => row.original.kunde?.display_name ?? "—",
      },
      { accessorKey: "status", header: "Status", size: 100 },
    ],
    []
  );

  // Pagination-Handler
  const handlePaginationChange = (pagination) => {
    const next =
      typeof pagination === "function"
        ? pagination({ pageIndex, pageSize })
        : pagination;

    if (!next) return;

    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  return (
    <DataTable
      columns={columns}
      data={rows}
      pageTitle={pageTitle}
      pageSubtitle={pageSubtitle}
      isLoading={isLoading}
      hasLoaded={hasLoaded}
      enableHeader
      enableTopToolbar
      topToolbarIcons
      enableGlobalFilter
      enableColumnFilters
      enableColumnHiding
      enableRowExpansion
      renderRowExpansionContent={(row) => row._expand}
      enablePagination
      manualPagination
      pageIndex={pageIndex}
      pageSize={pageSize}
      totalRows={totalRows}
      onChangePage={handlePaginationChange}
      onChangeSorting={(sorting) =>
        updateTableState(tableKey, {
          sorting: applySortingWithAccessorKeys(sorting, columns),
        })
      }
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey="v2_table_bank_accounts"
      renderDetailPanel={({ row }) => (
        <Box sx={{ p: 2 }}>
          {Array.isArray(row.original.objekte) &&
          row.original.objekte.length > 0 ? (
            <DataTable
              columns={[
                {
                  accessorKey: "bezeichnung",
                  header: "Bezeichnung",
                  size: 200,
                },
                { accessorKey: "strasse", header: "Straße", size: 150 },
                { accessorKey: "hausnr", header: "Hausnummer", size: 100 },
                { accessorKey: "plz", header: "PLZ", size: 80 },
                { accessorKey: "ort", header: "Ort", size: 150 },
              ]}
              data={row.original.objekte}
              pageSize={99999}
              enableFooter={false}
              enableTopToolbar={false}
              enablePagination={false}
              enableColumnFilters={false}
              enableSorting={true}
              enableBottomToolbar={false}
              enableGlobalFilter={true}
              initialState={{
                globalFilter: tableState.searchword,
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ pl: 2 }}>
              Keine Objekte verknüpft
            </Typography>
          )}
        </Box>
      )}
    />
  );
}

BankAccountTable.propTypes = {
  pageTitle: PropTypes.string,
  pageSubtitle: PropTypes.string,
};
